import { OutProductExtendedViewDTO, SeoDTO } from "@app/bf-api";
import { formatMessage } from "@app/translations/intl";

import { getProductStructuredData } from "./get-product-structured-data";
import { JsonLdNode } from "@app/api/wp-page-fetcher/utils/to-json-ld";

/** Up to `limit` distinct, non-empty merchant ("winkel") names, in the order the API returns them. */
const getTopMerchantNames = (product: OutProductExtendedViewDTO, limit: number): string[] => {
  const names: string[] = [];

  for (const deal of product.deals ?? []) {
    const name = deal.merchant?.name?.trim();
    if (name && !names.includes(name)) {
      names.push(name);
      if (names.length === limit) break;
    }
  }

  return names;
};

/**
 * `formatMessage` echoes the key back when a locale is missing it, which would put
 * "productSingle.fallbackSeoTitle" in a page title. Treating that echo as empty lets callers fall
 * back with `||`.
 */
const translate = (id: string, values?: Record<string, string>): string => {
  const message = formatMessage({ id }, values);

  return message === id ? "" : message;
};

/** "X" → "X"; "X","Y" → "X en Y" — the conjunction comes from the platform's own language. */
const joinMerchantNames = (names: string[]): string => names.join(translate("productSingle.merchantJoin") || " ");

/**
 * Fallback meta title used when a product has no hand-written SEO title. Falls back to the bare
 * product title if the translation is unavailable, so the page is never titled with a message key.
 */
const getFallbackSeoTitle = (product: OutProductExtendedViewDTO): string =>
  translate("productSingle.fallbackSeoTitle", { productTitle: product.title }) || product.title;

/**
 * Fallback meta description used when a product has no hand-written SEO description.
 *
 * A separate message is used when no live merchants are known, so an offline page never renders a
 * dangling "van o.a.  en vind…". Both variants are full sentences rather than concatenated
 * fragments, because word order around the shop names differs per language.
 */
const getFallbackSeoDescription = (product: OutProductExtendedViewDTO): string => {
  const merchants = getTopMerchantNames(product, 2);

  const description = merchants.length
    ? translate("productSingle.fallbackSeoDescriptionMerchants", {
        productTitle: product.title,
        merchants: joinMerchantNames(merchants)
      })
    : translate("productSingle.fallbackSeoDescription", { productTitle: product.title });

  return description || product.title;
};

/**
 * The product's full SEO object with generated fallbacks applied. Hand-written SEO (from the API)
 * always wins — only missing fields are filled — so a product page is never indexed with an empty
 * meta title/description. Auto-generated pages (created by the sync) rely on this entirely.
 *
 * `jsonSchema` stays the hand-written block only. The generated structured data is returned as
 * nodes, resolved with the same title and description, so the WebPage describes the page under the
 * name it actually renders. The page merges those nodes into its single `@graph`.
 *
 * With a hand-written block there are no generated nodes and no deals to leave out of the graph:
 * we cannot know which offers that block describes, so every deal tile keeps its own Product.
 */
export const getProductSeo = (
  product: OutProductExtendedViewDTO,
  platformId: number,
  pageUrl: string
): { seo: SeoDTO; structuredDataNodes?: JsonLdNode[]; mainEntityDealIds: number[] } => {
  const title = product.seo?.title || getFallbackSeoTitle(product);
  const description = product.seo?.description || getFallbackSeoDescription(product);
  const seo = { ...product.seo, title, description };

  if (product.seo?.jsonSchema) return { seo, mainEntityDealIds: [] };

  const { nodes, offerDealIds } = getProductStructuredData({
    product,
    platformId,
    pageUrl,
    seoTitle: title,
    seoDescription: description
  });

  return { seo, structuredDataNodes: nodes, mainEntityDealIds: offerDealIds };
};
